import { FetchError } from 'ofetch'
import { navigateTo } from '#imports'
import type { PaAPI, UnleashFeatureFlag } from '@rialtic/types'
import { acceptHMRUpdate, defineStore } from 'pinia'

export const useAppConfigurationUnleashed = defineStore(
  'app-config-unleashed',
  {
    state: () => ({
      featureFlags: [] as UnleashFeatureFlag[],
    }),

    actions: {
      async getAll() {
        if (import.meta.server) {
          return this.$state
        }

        const { $datadog, $auth0, $auth0Ready } = useNuxtApp()
        const auth0Client = $auth0()
        await $auth0Ready()

        if (!auth0Client.isAuthenticated.value) {
          if (import.meta.client) {
            await navigateTo('/auth/login')
          }
          return this.$state
        }

        try {
          const apiFetch = $apiFetcher()
          const data = await apiFetch<PaAPI.Get['/features']>(
            '/api/paapi/features',
          )
          this.featureFlags = data as UnleashFeatureFlag[]
        } catch (error) {
          const shouldRedirect =
            error instanceof FetchError &&
            [400, 401].includes(error.status ?? 0)

          if (shouldRedirect && import.meta.client) {
            await navigateTo('/auth/login')
            return this.$state
          }

          $datadog.addError(error, {
            function: 'stores/appConfig:getAllUnleash',
          })
        }

        return this.$state
      },
    },

    getters: {
      isUnleashFeatureEnabled: (state) => {
        return (featureName: string): boolean => {
          return state.featureFlags?.length
            ? state.featureFlags.some(
                (feature: UnleashFeatureFlag) =>
                  feature.name === featureName && feature.enabled,
              )
            : false
        }
      },
    },
  },
)

if (import.meta.hot)
  import.meta.hot.accept(
    acceptHMRUpdate(useAppConfigurationUnleashed, import.meta.hot),
  )
